import './DataConnectorTool.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Intent } from '@Components/designConstants';
import { ModalEditWidget } from '@Components/ModalEditWidget/ModalEditWidget';
import {
  CheckIsValidFn,
  SaveChangesInput,
  SelectorOptions
} from '@Components/ModalEditWidget/modal-edit-constants';
import { Page } from '@Components/Page/Page';
import { Spinner } from '@Components/Spinner';
import { TableEmptyState } from '@Components/TableEmptyState';
import { useAppSelector } from '@src/redux';
import { useSetAreYouSure, useSetError } from '@src/redux/modals';
import { isNOCSelector } from '@src/redux/user';
import { useCompanyIds } from '@src/utils/hooks/useCompanyIds';
import {
  buildDataConnectorHeaders,
  DataConnectorRow,
  getDataConnectorRowId,
  PAGE_TITLE,
  PAGE_TYPE,
  ROW_HEIGHT,
  SECRET_OPTIONS,
  SOURCE_OPTIONS
} from './data-connector-tool-constants';
import {
  CheckAuthParams,
  checkDataConnectorRowIsValid,
  debounceAuthCheck
} from './data-connector-tool-utils';
import {
  checkDataConnectorAuth,
  useDataConnectorRows,
  useSaveDataConnectorRows,
  useToggleDataConnectorRow
} from './use-data-connector-tool';

export const DataConnectorTool = () => {
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();
  const isNOC = useAppSelector(isNOCSelector);

  const { data: rows, isLoading: rowsLoading, error: rowsError } = useDataConnectorRows();
  const { data: companyOptions, error: companyOptionsError } = useCompanyIds();
  // The mutation object returned by useMutation is recreated every render; mutate/mutateAsync are
  // the stable part.
  const { mutateAsync: saveDataConnectorRows, isPending: isSaving } = useSaveDataConnectorRows();
  const { mutate: toggleRow } = useToggleDataConnectorRow();

  const [checkAuthLoadingKey, setCheckAuthLoadingKey] = useState<string | null>(null);

  useEffect(() => {
    if (rowsError) {
      setError({
        title: "Couldn't load data connectors",
        message: `Failed to get data connector data. ERROR: ${rowsError.message}`,
        reportError: rowsError
      });
    }
  }, [rowsError, setError]);

  useEffect(() => {
    if (companyOptionsError) {
      setError({
        message: `Error fetching company ids. Error: ${companyOptionsError.message}`,
        reportError: companyOptionsError
      });
    }
  }, [companyOptionsError, setError]);

  const selectorOptions: SelectorOptions = useMemo(
    () => ({
      company_options: (companyOptions ?? []).map(value => ({ value, label: value })),
      source_options: SOURCE_OPTIONS,
      secret_options: SECRET_OPTIONS
    }),
    [companyOptions]
  );

  const runAuthCheck = useCallback(async (params: CheckAuthParams): Promise<boolean> => {
    setCheckAuthLoadingKey(getDataConnectorRowId(params));
    try {
      return await checkDataConnectorAuth(params);
    } finally {
      setCheckAuthLoadingKey(null);
    }
  }, []);

  const debouncedAuthCheck = useMemo(() => debounceAuthCheck(runAuthCheck), [runAuthCheck]);

  const onCheckAuth = useCallback(
    async (row: DataConnectorRow) => {
      const params: CheckAuthParams = {
        company: row.company,
        source: row.source,
        account_id: row.account_id,
        secret_path: row.secret_path,
        mcc_id: row.mcc_id
      };
      const authCheck = await runAuthCheck(params);
      if (authCheck) {
        setError({
          title: 'Success',
          intent: Intent.SUCCESS,
          message: `Auth was validated for ${row.company}, ${row.source}, ${row.account_id}, ${row.secret_path}.`
        });
      } else {
        const error = new Error(
          `Auth Failed for ${row.company}, ${row.source}, ${row.account_id}, ${row.secret_path}.`
        );
        setError({ message: error.message, reportError: error });
      }
    },
    [runAuthCheck, setError]
  );

  const onToggleEnabled = useCallback(
    (row: DataConnectorRow) => {
      // setAreYouSure's promise rejects when the user cancels -- catch it here (there's nothing
      // to do on cancel) so that rejection doesn't surface as an unhandled promise rejection.
      setAreYouSure({
        title: row.enabled ? 'Set enabled column to false?' : 'Set enabled column to true?',
        message: `You're about to set the enabled column in mobius_data_connectors to ${
          row.enabled ? 'false' : 'true'
        }. Are you sure you want to continue?`,
        cancelText: 'Cancel',
        okayText: 'Continue',
        onOkay: () => {
          toggleRow(
            { accountId: row.account_id, source: row.source, enabled: !row.enabled },
            {
              onError: e =>
                setError({
                  message: `Failed to toggle data connector row. ERROR: ${e.message}`,
                  reportError: e
                })
            }
          );
        }
      }).catch(() => {});
    },
    [setAreYouSure, setError, toggleRow]
  );

  const headers = useMemo(
    () => buildDataConnectorHeaders(checkAuthLoadingKey, onCheckAuth, onToggleEnabled),
    [checkAuthLoadingKey, onCheckAuth, onToggleEnabled]
  );

  const checkIsValid: CheckIsValidFn<DataConnectorRow> = useCallback(
    (data, handleInvalidMessage) =>
      checkDataConnectorRowIsValid(data, rows, debouncedAuthCheck, handleInvalidMessage),
    [rows, debouncedAuthCheck]
  );

  const onSaveModal = useCallback(
    async ({ addedData, modifiedData, cleanup }: SaveChangesInput<DataConnectorRow>) => {
      // putMobiusDataConnectorJob only ever processes `insert` -- an edit to an existing row has
      // no way to persist.
      const hasAdds = addedData.length > 0;
      const hasDiscardedEdits = modifiedData.length > 0;
      try {
        if (hasAdds) {
          await saveDataConnectorRows(addedData);
        }
        // Only the built-in "Changes Saved" dialog when something was actually saved -- an
        // edit-only batch has nothing to persist, so showing it would claim a save that didn't
        // happen.
        cleanup({ showSuccessDialog: hasAdds });
        if (hasDiscardedEdits) {
          setError({
            intent: Intent.WARNING,
            message: hasAdds
              ? "Edits to existing connectors aren't supported yet, so that change wasn't saved. The new rows in this batch were saved."
              : "Edits to existing connectors aren't supported yet, so this change wasn't saved."
          });
        }
      } catch (e) {
        setError({
          message: `Failed to update data connectors. ERROR: ${e.message}`,
          reportError: e
        });
      }
    },
    [saveDataConnectorRows, setError]
  );

  return (
    <Page
      title={PAGE_TITLE}
      pageType={PAGE_TYPE}
      minHeight='600px'
      isLoading={rowsLoading}
      actions={isSaving ? <Spinner color='white' /> : undefined}
    >
      <div className='data-connector-tool-page'>
        <ModalEditWidget<DataConnectorRow>
          className='data-connector-tool-table'
          dataTestId='data-connector-tool-table'
          headers={headers}
          initialTableData={rows ?? []}
          onSaveModal={onSaveModal}
          selectorOptions={selectorOptions}
          enableAdd={isNOC}
          // putMobiusDataConnectorJob only ever processes `insert` (see
          // data-connector-tool-utils.ts) -- ModalEditWidget defaults enableDelete to true, which
          // would show a working-looking Delete button that silently no-ops server-side and the
          // row reappears once the post-save refetch lands.
          enableDelete={false}
          checkIsValid={checkIsValid}
          rowHeight={ROW_HEIGHT}
          getRowId={getDataConnectorRowId}
          isLoading={rowsLoading}
          // Only set on error -- ModalEditWidget hides its header Add button whenever
          // emptyContent is set and the loaded table has zero rows.
          emptyContent={rowsError ? <TableEmptyState message="Couldn't load data" /> : undefined}
        />
      </div>
    </Page>
  );
};
